import { gql } from 'graphql-request';

import {
  AccessProfile,
  Contact,
  Membership,
  MembershipOfferings,
  MembershipType,
  PaymentMethod,
  Subscription,
} from '../types';
import { axiosQuery } from '../utils/axiosQuery';

export interface GetAccountResponse {
  account: {
    email: string;
    createdAt: string | null;
    dunningLevel: number | null;
    shipToContact: Contact | null;
    billToContact: Contact | null;
    membershipType: MembershipType | null;
    currentSubscription: Subscription | null;
    subscriptions: Subscription[];
    membership: Membership | null;
    accessProfile: AccessProfile | null;
    paymentMethods: PaymentMethod[];
    isValidCurrency: boolean; // checks if currency is valid to update PM or rate plan
    b2bPartnerName: string | null; // ex) Essence Healthcare
  };
  membershipOffering: MembershipOfferings;
}

interface GetAccountArgs {
  userAccessProfileFlag: boolean;
}

const getAccountQuery = ({ userAccessProfileFlag }: GetAccountArgs = { userAccessProfileFlag: false }) => gql`
  query GetAccountV2 {
    membershipOffering {
      eligibleForPartnership
      prepaidMonths
      membershipStatus {
        type
        subscriptionFound
        signupComplete
      }
      channel
      entitlement {
        type
      }
      customerId
      customerName
    }
    accountV2 {
      email
      dunningLevel
      createdAt
      shipToContact {
        firstName
        lastName
        address1
        address2
        city
        country
        postalCode
        state
        personalEmail
        addressValidated
      }
      billToContact {
        firstName
        lastName
        address1
        address2
        city
        country
        postalCode
        state
        personalEmail
        addressValidated
      }
      paymentMethods {
        ... on ApiCreditCardPaymentMethod {
          type
          isDefault
          status
          cardNumber
          isExpired
          isReusableInRenew
          cardType
        }
        ... on ApiPayPalPaymentMethod {
          type
          isDefault
          status
          isReusableInRenew
        }
      }
      membershipType
      isValidCurrency
      b2bPartnerName
      currentSubscription {
        id
        channel
        subscriptionType
        renewalState
        subscriptionState
        nextBillingDate
        nextPaymentDate
        endDate
        startDate
        pendingCancellation
        pendingCompMonths
        trialExtensionLengthInMonths
        recurringFee {
          amount
          billingPeriod
          currency
        }
        alternativeBillingPeriods {
          amount
          billingPeriod
          currency
        }
        prepaidPeriods {
          current {
            start
            end
            lengthInMonths
          }
          future {
            start
            end
            lengthInMonths
          }
        }
      }
      subscriptions {
        id
        channel
        subscriptionType
        renewalState
        subscriptionState
        nextBillingDate
        endDate
        startDate
        pendingCancellation
        trialExtensionLengthInMonths
        recurringFee {
          amount
          billingPeriod
          currency
        }
        alternativeBillingPeriods {
          amount
          billingPeriod
          currency
        }
      }${
        userAccessProfileFlag
          ? `
      accessProfile {
        permissions
        userContext {
          state
          payerType
          subscriptionModel
          planType
        }
      }
      membership {
        product
        name
        nextPaymentDate
        pendingCancellation
        inDunning
        active
        plans {
          currency
          endDate
          payerType
          periodLength
          periodType
          price
          startDate
          status
          subscriptionId
          subscriptionModel
          type
        }
      }`
          : ``
      }
    }
  }
`;

type ApiResponse = Omit<GetAccountResponse, 'account'> & { accountV2: GetAccountResponse['account'] };

export const getAccount = (args?: GetAccountArgs) =>
  axiosQuery<ApiResponse>('GetAccountV2', getAccountQuery(args)).then((response) => {
    const { accountV2: account, ...rest } = response;
    return {
      ...rest,
      account,
    };
  });
